import { camelCase } from 'change-case'
import * as styles from './styles'

/**
 * ex:
 * const richTextShortcuts = {
 *  'Table Shortcuts': {
 *     'Add new table': 'meta + alt + t',
 *     'Add column left': 'meta + left arrow',
 *   },
 *  'Other Shortcuts': {
 *     'Focus editor': 'meta + e',
 *   },
 * }
 */
type ShortcutMap = {
  [group: string]: {
    [action: string]: string
  }
}

export type ShortcutListProps = {
  className?: string
  includeGroupHeadings?: boolean
  shortcutMap: ShortcutMap
}

const uaIsMac = /mac/i.test(window.navigator.userAgent) && 'mac'
const uaIsLinux = /linux/i.test(window.navigator.userAgent) && 'linux'
export const localizeCommandsForOS = (
  command: string,
  os: 'win' | 'mac' | 'linux' = uaIsMac || uaIsLinux || 'win'
) => {
  if (os === 'mac') {
    return command
      .replace('meta', 'cmd')
      .replace('alt', 'opt')
      .replace('backspace', 'delete')
      .replace('enter', 'return')
  }
  if (os === 'linux') {
    return command.replace('meta', 'super')
  }

  return command.replace('meta', 'win')
}

/**
 * Renders a list of actions with their associated keyboard shortcuts.
 *
 * This component uses standardized names for the following keys:
 * - "meta": Windows key (Windows) / Command key (macOS) / Super key (Linux)
 * - "alt": Alt key (Windows, Linux) / Option key (macOS)
 * - "backspace": Backspace key (Windows, Linux) / Delete key (macOS)
 * - "enter": Enter key (Windows, Linux) / Return key (macOS)
 */
export const ShortcutList: React.FC<ShortcutListProps> = ({
  className,
  includeGroupHeadings = true,
  shortcutMap,
}) => {
  return (
    <div css={styles.container} className={className}>
      {Object.keys(shortcutMap).map((group) => {
        const groupId = `shortcut-list--${camelCase(group)}`

        return (
          <section
            css={styles.group}
            key={group}
            aria-label={!includeGroupHeadings ? group : undefined}
            aria-labelledby={includeGroupHeadings ? groupId : undefined}
          >
            {includeGroupHeadings && (
              <span id={groupId} css={styles.groupHeading}>
                {group}
              </span>
            )}
            <dl css={styles.list}>
              {Object.keys(shortcutMap[group]).map((action) => (
                <div css={styles.listItem} key={action}>
                  <dt role="listitem" css={styles.action}>
                    {action}
                  </dt>
                  <dd css={styles.command}>
                    {localizeCommandsForOS(shortcutMap[group][action])}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )
      })}
    </div>
  )
}
